import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  ClipboardList,
  Calendar,
  Users,
  UserCircle,
  FileText,
  Calculator,
  Package,
  BarChart3,
  Settings,
  Wrench,
  RefreshCw,
  MapPin,
  CalendarPlus,
  Trophy,
  Star,
  LayoutGrid,
} from 'lucide-react';
import { cn } from '@/utils/cn';
import {
  Sidebar,
  SidebarHeader,
  SidebarContent,
  SidebarSection,
  SidebarItem,
  SidebarFooter,
  SidebarToggle,
} from '@/components/ui/Sidebar';
import { Avatar } from '@/components/ui/Avatar';
import { WorsaasLogomark } from '@/components/WorsaasLogo';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { Header } from '@/components/layout/Header';
import { MobileNav } from '@/components/layout/MobileNav';
import CommandPalette from '@/components/CommandPalette';
import { useUIStore } from '@/stores/uiStore';
import { useAuthStore } from '@/stores/authStore';
import { useGpsTracker } from '@/hooks/useGps';
import { useSubscriptionStatus } from '@/hooks/useSubscriptionStatus';

type NavItem = {
  labelKey: string;
  href: string;
  icon: typeof LayoutDashboard;
  end?: boolean;
  permission?: string;
};

const mainNav: NavItem[] = [
  { labelKey: 'nav.dashboard', href: '/', icon: LayoutDashboard, end: true },
  { labelKey: 'nav.workOrders', href: '/work-orders', icon: ClipboardList, permission: 'work_orders:view' },
  { labelKey: 'nav.myWork', href: '/my-work', icon: Wrench, permission: 'work_orders:view' },
  { labelKey: 'nav.dispatch', href: '/dispatch', icon: LayoutGrid, permission: 'calendar:view' },
  { labelKey: 'nav.calendar', href: '/calendar', icon: Calendar, permission: 'calendar:view' },
  { labelKey: 'nav.customers', href: '/customers', icon: Users, permission: 'customers:view' },
  { labelKey: 'nav.team', href: '/team', icon: UserCircle, permission: 'team:view' },
];

const gestionNav: NavItem[] = [
  { labelKey: 'nav.invoices', href: '/invoices', icon: FileText, permission: 'invoices:view' },
  { labelKey: 'nav.estimates', href: '/estimates', icon: Calculator, permission: 'estimates:view' },
  { labelKey: 'nav.contracts', href: '/contracts', icon: RefreshCw, permission: 'contracts:view' },
  { labelKey: 'nav.bookings', href: '/bookings', icon: CalendarPlus, permission: 'bookings:view' },
  { labelKey: 'nav.inventory', href: '/inventory', icon: Package, permission: 'inventory:view' },
  { labelKey: 'nav.tracking', href: '/tracking', icon: MapPin, permission: 'tracking:view' },
  { labelKey: 'nav.reports', href: '/reports', icon: BarChart3, permission: 'reports:view' },
  { labelKey: 'nav.csat', href: '/reports/csat', icon: Star, permission: 'csat:view' },
  { labelKey: 'nav.leaderboard', href: '/leaderboard', icon: Trophy, permission: 'gamification:view' },
];

const footerNav: NavItem[] = [
  { labelKey: 'nav.settings', href: '/settings', icon: Settings, permission: 'settings:view' },
];

// ──────────────────────────────────────────────
// AppLayout — Main application layout with sidebar, header, and content
// ──────────────────────────────────────────────

function AppLayout() {
  const { t } = useTranslation();
  const sidebarCollapsed = useUIStore((s) => s.sidebarCollapsed);
  const setSidebarCollapsed = useUIStore((s) => s.setSidebarCollapsed);
  const user = useAuthStore((s) => s.user);
  const org = useAuthStore((s) => s.org);
  const permissions = useAuthStore((s) => s.permissions);
  const location = useLocation();
  const navigate = useNavigate();

  // GPS tracker: sends technician position to backend every 60s
  useGpsTracker();

  const { isReadOnly, isCancelling, cancelDaysRemaining } = useSubscriptionStatus();
  const canSeeBilling = permissions.includes('*') || permissions.includes('billing:manage');

  function isActive(href: string, end = false) {
    return end
      ? location.pathname === href
      : location.pathname.startsWith(href);
  }

  const filterByPermission = (items: NavItem[]) =>
    items.filter((item) => {
      if (!item.permission) return true;
      return permissions.includes('*') || permissions.includes(item.permission);
    });

  const visibleMain = useMemo(() => filterByPermission(mainNav), [permissions]);
  const visibleGestion = useMemo(() => filterByPermission(gestionNav), [permissions]);
  const visibleFooter = useMemo(() => filterByPermission(footerNav), [permissions]);

  return (
    <div className="flex h-screen overflow-hidden bg-slate-50 dark:bg-slate-950">
      {/* Sidebar — hidden on mobile */}
      <div className="hidden md:flex">
        <Sidebar
          collapsed={sidebarCollapsed}
          onCollapsedChange={setSidebarCollapsed}
        >
          {/* Brand */}
          <SidebarHeader>
            {sidebarCollapsed ? (
              <WorsaasLogomark size={32} />
            ) : (
              <div className="flex items-center gap-2.5">
                <WorsaasLogomark size={32} className="shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold tracking-tight text-slate-900 dark:text-white" style={{ letterSpacing: '-0.5px' }}>
                    {org?.name ?? 'WorSaaS'}
                  </p>
                </div>
              </div>
            )}
          </SidebarHeader>

          {/* Navigation */}
          <SidebarContent>
            <SidebarSection>
              {visibleMain.map((item) => (
                <SidebarItem
                  key={item.href}
                  icon={<item.icon />}
                  active={isActive(item.href, item.end)}
                  onClick={() => navigate(item.href)}
                >
                  {t(item.labelKey)}
                </SidebarItem>
              ))}
            </SidebarSection>

            {visibleGestion.length > 0 && (
              <SidebarSection label="Gestion">
                {visibleGestion.map((item) => (
                  <SidebarItem
                    key={item.href}
                    icon={<item.icon />}
                    active={isActive(item.href)}
                    onClick={() => navigate(item.href)}
                  >
                    {t(item.labelKey)}
                  </SidebarItem>
                ))}
              </SidebarSection>
            )}
          </SidebarContent>

          {/* Footer */}
          <SidebarFooter>
            {visibleFooter.map((item) => (
              <SidebarItem
                key={item.href}
                icon={<item.icon />}
                active={isActive(item.href)}
                onClick={() => navigate(item.href)}
              >
                {t(item.labelKey)}
              </SidebarItem>
            ))}

            <div className="mt-2 flex items-center justify-between">
              <SidebarToggle />
            </div>

            {/* User info at bottom (when expanded) */}
            {!sidebarCollapsed && user && (
              <div className="mt-3 flex items-center gap-3 rounded-lg border border-slate-100 px-3 py-2.5 dark:border-slate-800">
                <Avatar
                  name={user.name}
                  src={user.avatarUrl}
                  size="sm"
                />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-slate-900 dark:text-white">
                    {user.name}
                  </p>
                  <p className="truncate text-xs text-slate-500">{user.email}</p>
                </div>
              </div>
            )}
          </SidebarFooter>
        </Sidebar>
      </div>

      {/* Main area */}
      <div
        className={cn(
          'flex flex-1 flex-col overflow-hidden',
          'transition-[margin] duration-300 ease-in-out'
        )}
      >
        {/* Cancellation countdown banner — only visible to billing managers */}
        {canSeeBilling && isCancelling && cancelDaysRemaining > 0 && !isReadOnly && (
          <div className="flex items-center justify-center gap-3 bg-amber-500 px-4 py-2 text-white">
            <span className="text-sm font-medium">
              Tu suscripcion se cancela en {cancelDaysRemaining} {cancelDaysRemaining === 1 ? 'dia' : 'dias'}.
            </span>
            <button
              type="button"
              onClick={() => navigate('/settings?tab=billing')}
              className="rounded-md bg-white/20 px-3 py-0.5 text-sm font-semibold hover:bg-white/30 transition-colors"
            >
              Renovar plan
            </button>
          </div>
        )}

        {/* Read-only banner when subscription expired — only visible to billing managers */}
        {canSeeBilling && isReadOnly && (
          <div className="flex items-center justify-center gap-3 bg-rose-600 px-4 py-2 text-white">
            <span className="text-sm font-medium">
              Modo solo lectura — Tu periodo de prueba ha terminado.
            </span>
            <button
              type="button"
              onClick={() => navigate('/settings?tab=billing')}
              className="rounded-md bg-white/20 px-3 py-0.5 text-sm font-semibold hover:bg-white/30 transition-colors"
            >
              Suscribete ahora
            </button>
          </div>
        )}

        {/* Header */}
        <Header />

        {/* Page content */}
        <main
          className={cn(
            'custom-scrollbar flex-1 overflow-y-auto',
            // Bottom padding on mobile for the fixed bottom nav
            'pb-20 md:pb-0'
          )}
        >
          <div className="mx-auto w-full max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
            <ErrorBoundary>
              <Outlet />
            </ErrorBoundary>
          </div>
        </main>
      </div>

      {/* Mobile bottom navigation */}
      <MobileNav />

      {/* Command Palette (Cmd+K) */}
      <CommandPalette />
    </div>
  );
}

AppLayout.displayName = 'AppLayout';

export { AppLayout };
